type Count = number | null | undefined;

/** What the server says a remap changes in Sage and Jira; a part is null when that integration is unaffected. */
export type RemapImpact =
	| {
			readonly sage?: {
				readonly activated?: Count;
				readonly created?: Count;
				readonly deactivated?: Count;
				readonly timeBlocked?: boolean | null;
			} | null;
			readonly jira?: {readonly stopSyncing?: Count; readonly startSyncing?: Count} | null;
	  }
	| null
	| undefined;

/**
 * One sentence of the remap warning: a message id, the project count it is about when it has one, and the id of a
 * quieter sentence explaining it when it needs one.
 */
export type RemapImpactLine = {id: string; count?: number; detailId?: string};

/** The lines about one integration, under a label with the integration's name. */
export type RemapImpactSection = {id: string; lines: RemapImpactLine[]};

const messageId = (name: string) => `settings.project_stages.remap_impact.${name}`;

const line = (name: string, count?: number, detail?: string): RemapImpactLine => ({
	id: messageId(name),
	...(count === undefined ? {} : {count}),
	...(detail === undefined ? {} : {detailId: messageId(detail)}),
});

const sageLines = (sage: NonNullable<RemapImpact>['sage']): RemapImpactLine[] => {
	const lines: RemapImpactLine[] = [];
	if (sage?.deactivated) {
		lines.push(line('sage_deactivated', sage.deactivated));
		if (sage.timeBlocked) {
			lines.push(line('sage_time_blocked'));
		}
	}
	if (sage?.activated) {
		lines.push(line('sage_activated', sage.activated));
	}
	if (sage?.created) {
		lines.push(line('sage_created', sage.created));
	}
	return lines;
};

const jiraLines = (jira: NonNullable<RemapImpact>['jira']): RemapImpactLine[] => {
	const lines: RemapImpactLine[] = [];
	if (jira?.stopSyncing) {
		lines.push(line('jira_stop', jira.stopSyncing, 'jira_stop_detail'));
	}
	if (jira?.startSyncing) {
		lines.push(line('jira_start', jira.startSyncing));
	}
	return lines;
};

/** The warning's sections, Sage first, leaving out every change that affects no project and every empty section. */
export const remapImpactSections = (impact: RemapImpact): RemapImpactSection[] =>
	[
		{id: messageId('sage_section'), lines: sageLines(impact?.sage)},
		{id: messageId('jira_section'), lines: jiraLines(impact?.jira)},
	].filter(section => section.lines.length > 0);
